import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Sparkles, CalendarCheck, MessageCircle, BadgeIndianRupee, ArrowRight, Star, Heart, Scissors, Brush, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, priceLabel } from "@/lib/api";
import { ArtistCard } from "@/components/ArtistCard";
import { WorkSlideshow } from "@/components/WorkSlideshow";
import { SEO } from "@/components/SEO";
import { PORTRAIT_URL, LOGO_URL, BANNER_URL, PHONE, PHONE_LINK, TAGLINE } from "@/lib/brand";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

const FAQS = [
  { q: "Do I need to create an account to book?", a: "No. Simply browse our artists and submit the enquiry form — no registration, login or password needed. Our team will contact you to confirm availability and booking." },
  { q: "How do I pay for my booking?", a: "There is no online payment. Once your booking is confirmed, payment is collected personally — we keep it simple and transparent." },
  { q: "Are the artists available outside Lucknow?", a: "Yes. The Velvet Brush is based in Lucknow and available for travel across India. Travel charges may apply and will be discussed during confirmation." },
  { q: "Is my booking guaranteed when I submit an enquiry?", a: "An enquiry is a request, not a confirmed booking. All bookings are subject to availability and confirmation by our team, who will call you shortly after you enquire." },
  { q: "Can I get a makeup trial before my event?", a: "Absolutely. We offer pre-event makeup trials so you can finalise your look with complete confidence. Select 'Makeup Trial' in the enquiry form." },
];

export default function Home() {
  const [artists, setArtists] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => {
      const deduped = Array.from(new Map(r.data.map((a) => [a.id, a])).values());
      setArtists(deduped.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)));
    }).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const gridArtists = artists.slice(1, 4);

  return (
    <div data-testid="home-page">
      <SEO
        title="Makeup Artists in Lucknow"
        description="Discover talented makeup artists for bridal, engagement and special occasions. Lucknow | Available for Travel. Call 8318512668."
        path="/"
      />

      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blush/40 blur-3xl" />
        <div className="absolute top-40 -left-24 w-72 h-72 rounded-full bg-sage/40 blur-3xl" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pt-6 pb-10 sm:pt-16 sm:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
            <img src={LOGO_URL} alt="The Velvet Brush" data-testid="hero-logo" className="h-20 sm:h-28 w-auto mb-5 sm:mb-6" />
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p data-testid="hero-tagline" className="text-xs uppercase tracking-[0.35em] font-semibold text-velvet">{TAGLINE}</p>
            </div>
            <h1 className="mt-4 font-serif font-bold text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              Your beauty.<br />Your artist.<br /><span className="italic text-velvet">Your moment.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed max-w-md">
              Discover talented makeup artists for bridal, engagement and special occasions.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/makeup-artists">
                <Button data-testid="hero-find-artist-btn" className="w-full sm:w-auto rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold px-8 h-12 text-base active:scale-95 transition-transform">
                  Find Your Makeup Artist
                </Button>
              </Link>
              <Link to="/enquire">
                <Button data-testid="hero-book-enquire-btn" variant="outline" className="w-full sm:w-auto rounded-full border-ink/25 text-ink px-8 h-12 text-base hover:bg-cream">
                  Book / Enquire
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-col gap-2 text-sm text-ink/70">
              <span data-testid="hero-location" className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-velvet" /> Lucknow <span className="text-ink/30">|</span> Available for Travel
              </span>
              <a href={PHONE_LINK} data-testid="hero-call" className="flex items-center gap-2 hover:text-ink transition-colors">
                <Phone className="w-4 h-4 text-velvet" /> Call {PHONE} for further details
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-blush/50 via-transparent to-vblue/30 -z-10" />
            <img
              src={PORTRAIT_URL}
              alt="The Velvet Brush makeup artist"
              data-testid="hero-portrait"
              className="w-full rounded-[2rem] object-cover object-top aspect-[4/3] sm:aspect-[4/5] max-h-[360px] sm:max-h-[560px] shadow-2xl"
            />
            <div className="absolute -top-4 right-4 sm:-right-5 bg-ink text-ivory rounded-2xl px-4 py-3 shadow-xl rotate-3" data-testid="hero-rating-badge">
              <p className="font-serif text-2xl leading-none">4.9<span className="text-gold text-lg"> ★</span></p>
              <p className="text-[10px] uppercase tracking-widest text-ivory/60 mt-1">Client rating</p>
            </div>
            <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-ivory/80 border border-white/50 rounded-2xl p-4 flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-velvet shrink-0" />
              <p className="text-sm text-ink/80">Bridal • Engagement • Party • HD & Airbrush • Hairstyling</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE BAND */}
      <div className="bg-ink py-4 overflow-hidden" data-testid="services-marquee">
        <div className="marquee-track flex whitespace-nowrap w-max">
          {[0, 1].map((n) => (
            <div key={n} className="flex items-center gap-10 pr-10 font-serif italic text-xl text-ivory/80">
              {["Bridal", "Engagement", "Reception", "Party Glam", "HD & Airbrush", "Hairstyling", "Draping", "Makeup Trials"].map((w) => (
                <span key={w} className="flex items-center gap-10">
                  {w}
                  <span className="w-1.5 h-1.5 rounded-full bg-velvet inline-block" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* OUR WORK — slideshow of real client looks */}
      {(artists[0]?.portfolio || []).length > 0 && (
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-24" data-testid="our-work-section">
        <motion.div {...fadeUp} className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Our Work</p>
            <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl lg:text-4xl">Real looks, real clients</h2>
          </div>
          <Link to={`/makeup-artists/${artists[0].slug}`} data-testid="view-portfolio-link" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-ink hover:text-velvet transition-colors">
            View portfolio <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <motion.div {...fadeUp} className="mt-8 max-w-3xl mx-auto">
          <WorkSlideshow images={artists[0].portfolio} />
        </motion.div>
      </section>
      )}

      {/* FEATURED ARTISTS — hidden when the only artist is already introduced in the hero */}
      {gridArtists.length > 0 && (
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-24" data-testid="featured-artists-section">
        <motion.div {...fadeUp} className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Featured Artists</p>
            <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl lg:text-4xl">Meet your artist</h2>
          </div>
          <Link to="/makeup-artists" data-testid="view-all-artists-link" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-ink hover:text-velvet transition-colors">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {gridArtists.map((a, i) => (
            <motion.div key={a.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
              <ArtistCard artist={a} />
            </motion.div>
          ))}
        </div>
        <Link to="/makeup-artists" className="sm:hidden mt-6 block">
          <Button data-testid="view-all-artists-mobile" variant="outline" className="w-full rounded-full border-ink/25">View All Artists</Button>
        </Link>
      </section>
      )}

      {/* SERVICES */}
      <section className="bg-white border-y border-[#EAE5DF]" data-testid="services-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-24">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Services</p>
            <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl lg:text-4xl">Looks for every celebration</h2>
          </motion.div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <motion.div key={s.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                data-testid={`home-service-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group bg-ivory rounded-2xl p-6 border border-[#EAE5DF] hover:border-blush hover:-translate-y-1 transition-[transform,border-color] duration-300">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif text-xl text-ink">{s.name}</h3>
                  <span className="text-sm font-semibold text-velvet whitespace-nowrap">{priceLabel(s)}</span>
                </div>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-8 text-center">
            <Link to="/services">
              <Button data-testid="view-all-services-btn" variant="outline" className="rounded-full border-ink/25 px-8">
                View All Services & Rates <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-24" data-testid="how-it-works-section">
        <motion.div {...fadeUp} className="text-center max-w-xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">How It Works</p>
          <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl lg:text-4xl">From enquiry to aisle, beautifully simple</h2>
        </motion.div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Brush, title: "Discover", text: "Browse our artists, portfolios, services and honest rates." },
            { icon: MessageCircle, title: "Enquire", text: "Submit a simple form — no account, no password, no fuss." },
            { icon: CalendarCheck, title: "Confirm", text: "Our team calls you to discuss availability, pricing and booking." },
            { icon: BadgeIndianRupee, title: "Pay Personally", text: "Payment is collected personally after booking confirmation." },
          ].map((step, i) => (
            <motion.div key={step.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              data-testid={`step-${step.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative pt-8 border-t-2 border-ink/10 hover:border-velvet transition-colors duration-300">
              <span className="font-serif italic text-5xl text-blush">0{i + 1}</span>
              <h3 className="mt-4 font-serif text-2xl text-ink">{step.title}</h3>
              <p className="mt-2 text-sm text-ink/60 leading-relaxed">{step.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-ink text-ivory" data-testid="why-choose-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blush">Why The Velvet Brush</p>
            <h2 className="mt-2 font-serif font-semibold text-2xl sm:text-3xl lg:text-4xl">Grace in every detail, glamour in every look</h2>
            <div className="mt-8 flex flex-col gap-6">
              {[
                { icon: ShieldCheck, title: "Verified artistry", text: "Premium products, hygiene-first kits and looks tailored to your features." },
                { icon: Heart, title: "No accounts, no pressure", text: "Enquire in under a minute. We call you — you decide in your own time." },
                { icon: Scissors, title: "Complete styling", text: "Makeup, hairstyling and draping under one roof for a seamless morning." },
                { icon: MapPin, title: "Lucknow & beyond", text: "Based in Lucknow, available for travel to your venue anywhere in India." },
              ].map((f) => (
                <div key={f.title} className="flex gap-4" data-testid={`why-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <f.icon className="w-6 h-6 text-velvet shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-serif text-lg">{f.title}</h3>
                    <p className="text-sm text-ivory/60 mt-1">{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
            {(artists[0]?.portfolio || []).slice(0, 4).map((p, i) => (
              <img key={i} src={p.url} alt={p.tag} loading="lazy"
                className={`rounded-2xl object-cover w-full aspect-[3/4] ${i % 2 === 1 ? "mt-8" : ""}`} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-24" data-testid="reviews-section">
        <motion.div {...fadeUp}>
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Client Reviews</p>
          <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl lg:text-4xl">Loved by brides & beyond</h2>
        </motion.div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((r, i) => (
            <motion.div key={r.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              data-testid={`review-${r.id}`}
              className="bg-white rounded-2xl p-6 border border-[#EAE5DF] flex flex-col">
              <div className="flex gap-0.5">
                {Array.from({ length: r.rating }).map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-gold fill-gold" />
                ))}
              </div>
              <p className="mt-4 text-sm text-ink/75 leading-relaxed flex-1">"{r.text}"</p>
              <div className="mt-4 pt-4 border-t border-[#EAE5DF]">
                <p className="font-semibold text-ink text-sm">{r.customer_name}</p>
                {r.event_type && <p className="text-xs text-velvet mt-0.5">{r.event_type}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-[#EAE5DF]" data-testid="faq-section">
        <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 sm:py-24">
          <motion.div {...fadeUp} className="text-center">
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">FAQ</p>
            <h2 className="mt-2 font-serif font-semibold text-ink text-2xl sm:text-3xl lg:text-4xl">Good to know</h2>
          </motion.div>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-serif text-lg text-ink">{f.q}</AccordionTrigger>
                <AccordionContent data-testid={`faq-a-${i}`} className="text-ink/65 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <img src={BANNER_URL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/85 to-blush/40" />
        <div className="relative px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 sm:py-28 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">Ready to book your look?</h2>
            <p className="mt-4 text-ink/65 max-w-lg mx-auto">Tell us about your event — our team will call you to confirm availability and pricing. No account needed.</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/enquire">
                <Button data-testid="final-cta-enquire-btn" className="w-full sm:w-auto rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold px-10 h-12 text-base active:scale-95 transition-transform">
                  Check Availability
                </Button>
              </Link>
              <a href={PHONE_LINK}>
                <Button data-testid="final-cta-call-btn" variant="outline" className="w-full sm:w-auto rounded-full border-ink/25 px-10 h-12 text-base">
                  <Phone className="w-4 h-4 mr-2" /> Call {PHONE}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
